/* eslint-disable no-restricted-globals, no-unused-vars */
// ─────────────────────────────────────────────────────────────────────────
// SERGA // PERSONAL OS — HUD overlay
// Topbar clock · TODAY'S FOCUS dashboard · breadcrumb + back · CONFIG panel.
// Detail now lives inside the cards themselves (see cards.js / world.js).
// ─────────────────────────────────────────────────────────────────────────
import { PROJECTS, TODAY } from './data.js';
import { gIcon, ZONE_GLYPH, PROJECT_GLYPH } from './glyphs.js';
import { $, el, trackedListeners } from './dom.js';

const STORE = 'serga-os-settings';
const fmtDue = d => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

export function createHUD(world, settings, onConfigChange) {
  // track persistent listeners/timers so the React component can tear them down
  // cleanly (StrictMode double-mounts the effect, so duplicates must be avoidable)
  const { on, dispose: disposeListeners } = trackedListeners();

  // ── clock ───────────────────────────────────────────────────────────────
  const clockEl = $('#clock');
  const tick = () => clockEl.textContent = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  tick();
  const clockTimer = setInterval(tick, 1000);

  // ── TODAY'S FOCUS dashboard ────────────────────────────────────────────────
  // Retired: Today's Focus now lives in the FEED tray (see tray.js). The #dash
  // element was removed from the markup; buildDash() no-ops if it's absent, so
  // the SHELL's "rebuild dash" callback stays harmless.
  const dash = $('#dash');
  const labelOf = p => p.title.split('—')[0].split('–')[0].split('(')[0].trim();
  function buildDash() {
    if (!dash) return;
    const cards = PROJECTS
      .map(p => ({ p, score: (p.priority === 'High' ? 100 : 50) + (p.due ? dueScore(p.due) : 0) }))
      .sort((a, b) => b.score - a.score).slice(0, 3);
    const wrap = el('div');
    wrap.appendChild(el('div', 'dhead', 'TODAY\u2019S FOCUS \u2014 ' + TODAY.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })));
    cards.forEach(({ p }) => {
      const c = el('div', 'dcard' + (p.priority === 'High' ? ' warn' : ''));
      const due = p.due ? 'DUE ' + fmtDue(p.due) : (p.tag || '');
      const gid = p.glyph || PROJECT_GLYPH[p.id] || 'cit';
      c.innerHTML = `<div class="dtop"><span class="dtitle">${gIcon(gid, 13, 'dmark')}<span>${labelOf(p)}</span></span>
        <span class="dpill ${p.priority === 'High' ? '' : 'med'}">${p.priority.toUpperCase()}</span></div>
        <div class="dnext">${p.next}</div><div class="dmeta" style="margin-top:7px">${due}</div>`;
      c.addEventListener('click', () => world.enterItemById('z-projects', 'p-' + p.id));
      wrap.appendChild(c);
    });
    dash.innerHTML = ''; dash.appendChild(wrap);
  }
  const dueScore = due => { const d = (new Date(due) - TODAY) / 86400000; return d < 0 ? 40 : Math.max(0, 30 - d); };
  buildDash();

  // ── breadcrumb + back ──────────────────────────────────────────────────────
  const crumbs = $('#crumbs'), backBtn = $('#backbtn');
  on(backBtn, 'click', () => world.back());
  on(document, 'keydown', e => { if (e.key === 'Escape') world.back(); });

  function updateNav(info) {
    document.body.classList.remove('lvl0', 'lvl1', 'lvl2');
    document.body.classList.add('lvl' + info.level);
    backBtn.classList.toggle('show', info.level === 1);

    const segs = [{ t: 'SERGA', go: () => world.gotoOverview(), cur: info.level === 0 }];
    if (info.zone) segs.push({ t: info.zone.data.label, g: ZONE_GLYPH[info.zone.data.id], go: () => world.gotoZone(info.zone.id), cur: info.level === 1 });
    if (info.item) segs.push({ t: shortTitle(info.item), cur: true });
    crumbs.innerHTML = '';
    segs.forEach((s, i) => {
      if (i) crumbs.appendChild(el('span', 'csep', '\u203a'));
      const inner = (s.g ? gIcon(s.g, 13, 'cgmark') : '') + '<span>' + s.t + '</span>';
      const a = el('span', 'cseg' + (s.cur ? ' cur' : ''), inner);
      if (!s.cur && s.go) a.addEventListener('click', s.go);
      crumbs.appendChild(a);
    });
    crumbs.classList.toggle('show', info.level > 0);
  }
  function shortTitle(item) {
    const d = item.data;
    return (d.title || d.label || '').split('—')[0].split('–')[0].split('(')[0].trim();
  }

  // ── CONFIG panel ────────────────────────────────────────────────────────────
  const cfg = $('#cfg');
  on($('#cfgbtn'), 'click', () => cfg.classList.toggle('open'));
  function bindSeg(id, key) {
    const wrap = $('#' + id);
    wrap.querySelectorAll('button').forEach(b => {
      b.classList.toggle('on', String(settings[key]) === b.dataset.v);
      on(b, 'click', () => {
        wrap.querySelectorAll('button').forEach(x => x.classList.remove('on'));
        b.classList.add('on');
        let v = b.dataset.v; if (v === 'true') v = true; else if (v === 'false') v = false;
        settings[key] = v; persist(); onConfigChange(key, v);
      });
    });
  }
  $('#cfg-accent').querySelectorAll('.swatch').forEach(s => {
    s.classList.toggle('on', s.dataset.v === settings.accent);
    on(s, 'click', () => {
      $('#cfg-accent').querySelectorAll('.swatch').forEach(x => x.classList.remove('on'));
      s.classList.add('on');
      settings.accent = s.dataset.v; persist(); onConfigChange('accent', s.dataset.v);
    });
  });
  bindSeg('cfg-scan', 'scan');
  bindSeg('cfg-stars', 'stars');
  bindSeg('cfg-motion', 'motion');
  // FIELD LAYOUT — drop every dragged card back to its default position
  const layoutReset = $('#cfg-layout') && $('#cfg-layout').querySelector('[data-act="reset"]');
  if (layoutReset) on(layoutReset, 'click', () => { world.resetLayout && world.resetLayout(); });
  const persist = () => { try { localStorage.setItem(STORE, JSON.stringify(settings)); } catch (e) {} };

  function dispose() {
    clearInterval(clockTimer);
    disposeListeners();
    if (dash) dash.innerHTML = '';
    if (crumbs) crumbs.innerHTML = '';
    document.body.classList.remove('lvl0', 'lvl1', 'lvl2');
  }

  return { buildDash, updateNav, dispose };
}

export function loadSettings() {
  const def = { accent: 'teal', scan: true, stars: true, motion: true };
  try { return { ...def, ...JSON.parse(localStorage.getItem(STORE) || '{}') }; }
  catch (e) { return def; }
}
